/**
 * Maintenance Service
 * Gerencia modo de manutenção e avisos da aplicação
 */

import { supabase } from '../supabaseClient';
import { logger } from '../logger';

// Configuração local (fallback)
let localMaintenanceConfig = {
  globalMaintenance: false,
  routesInMaintenance: [],
  announcements: [],
};

/**
 * Status de manutenção
 */
export const MaintenanceStatus = {
  ACTIVE: 'active',
  SCHEDULED: 'scheduled',
  COMPLETED: 'completed',
};

/**
 * Tipos de anúncio
 */
export const AnnouncementType = {
  INFO: 'info',
  WARNING: 'warning',
  ERROR: 'error',
  SUCCESS: 'success',
  MAINTENANCE: 'maintenance',
};

/**
 * Obtém configuração de manutenção
 */
export async function getMaintenanceConfig() {
  try {
    const { data, error } = await supabase
      .from('maintenance_config')
      .select('*')
      .single();

    if (error && error.code !== 'PGRST116') {
      throw error;
    }

    if (data) {
      localMaintenanceConfig = {
        globalMaintenance: data.global_maintenance || false,
        routesInMaintenance: data.routes_in_maintenance || [],
        announcements: data.announcements || [],
        maintenanceMessage: data.maintenance_message || 'Sistema em manutenção',
        estimatedEndTime: data.estimated_end_time,
        updatedAt: data.updated_at,
      };
    }

    return localMaintenanceConfig;
  } catch (error) {
    logger.warn('Usando config local de manutenção:', error.message);
    return localMaintenanceConfig;
  }
}

/**
 * Atualiza configuração de manutenção
 */
export async function updateMaintenanceConfig(config) {
  try {
    const { data: existing } = await supabase
      .from('maintenance_config')
      .select('id')
      .single();

    const payload = {
      global_maintenance: config.globalMaintenance ?? localMaintenanceConfig.globalMaintenance,
      routes_in_maintenance: config.routesInMaintenance ?? localMaintenanceConfig.routesInMaintenance,
      announcements: config.announcements ?? localMaintenanceConfig.announcements,
      maintenance_message: config.maintenanceMessage ?? localMaintenanceConfig.maintenanceMessage,
      estimated_end_time: config.estimatedEndTime ?? localMaintenanceConfig.estimatedEndTime,
      updated_at: new Date().toISOString(),
    };

    let result;
    if (existing?.id) {
      result = await supabase
        .from('maintenance_config')
        .update(payload)
        .eq('id', existing.id)
        .select()
        .single();
    } else {
      result = await supabase
        .from('maintenance_config')
        .insert(payload)
        .select()
        .single();
    }

    if (result.error) throw result.error;

    // Atualiza config local
    localMaintenanceConfig = {
      ...localMaintenanceConfig,
      ...config,
      updatedAt: payload.updated_at,
    };

    logger.log('✅ Configuração de manutenção atualizada');
    return result.data;
  } catch (error) {
    logger.error('Erro ao atualizar config:', error);
    throw error;
  }
}

/**
 * Verifica se uma rota específica está em manutenção
 */
export async function isRouteInMaintenance(route) {
  const config = await getMaintenanceConfig();

  // Se manutenção global está ativa, todas as rotas estão em manutenção
  if (config.globalMaintenance) {
    return true;
  }

  // Verifica se a rota específica está em manutenção
  return config.routesInMaintenance?.some(r => {
    // Suporta wildcards (ex: /admin/*)
    if (r.endsWith('*')) {
      const prefix = r.slice(0, -1);
      return route.startsWith(prefix);
    }
    return r === route;
  }) || false;
}

/**
 * Adiciona rota à lista de manutenção
 */
export async function addRouteToMaintenance(route, reason = '') {
  const config = await getMaintenanceConfig();
  const routes = config.routesInMaintenance || [];

  if (!routes.includes(route)) {
    routes.push(route);
    await updateMaintenanceConfig({
      routesInMaintenance: routes,
    });

    // Log da ação
    await logMaintenanceAction('add_route', { route, reason });
  }

  return routes;
}

/**
 * Remove rota da lista de manutenção
 */
export async function removeRouteFromMaintenance(route) {
  const config = await getMaintenanceConfig();
  const routes = (config.routesInMaintenance || []).filter(r => r !== route);

  await updateMaintenanceConfig({
    routesInMaintenance: routes,
  });

  // Log da ação
  await logMaintenanceAction('remove_route', { route });

  return routes;
}

/**
 * Ativa/desativa manutenção global
 */
export async function setGlobalMaintenance(active, message = '', estimatedEndTime = null) {
  await updateMaintenanceConfig({
    globalMaintenance: active,
    maintenanceMessage: message || 'Sistema em manutenção',
    estimatedEndTime,
  });

  // Log da ação
  await logMaintenanceAction(active ? 'enable_global' : 'disable_global', {
    message,
    estimatedEndTime,
  });

  return active;
}

/**
 * Cria um anúncio
 */
export async function createAnnouncement({
  title,
  message,
  type = AnnouncementType.INFO,
  routes = [], // Rotas onde mostrar (vazio = todas)
  startDate = null,
  endDate = null,
  dismissible = true,
}) {
  const config = await getMaintenanceConfig();
  const announcements = config.announcements || [];

  const newAnnouncement = {
    id: `ann_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    title,
    message,
    type,
    routes,
    startDate: startDate || new Date().toISOString(),
    endDate,
    dismissible,
    active: true,
    createdAt: new Date().toISOString(),
  };

  announcements.push(newAnnouncement);

  await updateMaintenanceConfig({
    announcements,
  });

  logger.log('📢 Anúncio criado:', newAnnouncement.id);
  return newAnnouncement;
}

/**
 * Remove um anúncio
 */
export async function removeAnnouncement(announcementId) {
  const config = await getMaintenanceConfig();
  const announcements = (config.announcements || []).filter(a => a.id !== announcementId);

  await updateMaintenanceConfig({
    announcements,
  });

  logger.log('🗑️ Anúncio removido:', announcementId);
  return announcements;
}

/**
 * Obtém anúncios ativos para uma rota
 */
export async function getActiveAnnouncements(route = null) {
  const config = await getMaintenanceConfig();
  const now = new Date();

  return (config.announcements || []).filter(announcement => {
    if (!announcement.active) return false;

    // Verifica data de início
    if (announcement.startDate && new Date(announcement.startDate) > now) {
      return false;
    }

    // Verifica data de fim
    if (announcement.endDate && new Date(announcement.endDate) < now) {
      return false;
    }

    // Verifica rota
    if (route && announcement.routes?.length > 0) {
      return announcement.routes.some(r => {
        if (r.endsWith('*')) {
          return route.startsWith(r.slice(0, -1));
        }
        return r === route;
      });
    }

    return true;
  });
}

/**
 * Atualiza um anúncio
 */
export async function updateAnnouncement(announcementId, updates) {
  const config = await getMaintenanceConfig();
  const announcements = (config.announcements || []).map(a => {
    if (a.id === announcementId) {
      return { ...a, ...updates, updatedAt: new Date().toISOString() };
    }
    return a;
  });

  await updateMaintenanceConfig({
    announcements,
  });

  logger.log('✏️ Anúncio atualizado:', announcementId);
  return announcements.find(a => a.id === announcementId);
}

/**
 * Registra ação de manutenção no log
 */
async function logMaintenanceAction(action, details = {}) {
  try {
    await supabase
      .from('maintenance_logs')
      .insert({
        action,
        details,
        created_at: new Date().toISOString(),
      });
  } catch (error) {
    logger.warn('Não foi possível registrar log de manutenção:', error.message);
  }
}

/**
 * Obtém histórico de ações de manutenção
 */
export async function getMaintenanceLogs(limit = 50) {
  try {
    const { data, error } = await supabase
      .from('maintenance_logs')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) throw error;
    return data || [];
  } catch (error) {
    logger.warn('Erro ao buscar logs de manutenção:', error.message);
    return [];
  }
}

/**
 * Obtém lista de rotas disponíveis na aplicação
 */
export function getAvailableRoutes() {
  return [
    { path: '/', name: 'Home', category: 'Public' },
    { path: '/user-login', name: 'Login', category: 'Auth' },
    { path: '/user-registration', name: 'Cadastro', category: 'Auth' },
    { path: '/forgot-password', name: 'Recuperar Senha', category: 'Auth' },
    { path: '/reset-password', name: 'Redefinir Senha', category: 'Auth' },
    { path: '/verify-email', name: 'Verificar Email', category: 'Auth' },
    { path: '/pricing', name: 'Planos', category: 'Public' },
    { path: '/chat-interface', name: 'Chat Dr. LEX', category: 'App' },
    { path: '/user-dashboard', name: 'Dashboard', category: 'App' },
    { path: '/conversation-history', name: 'Histórico', category: 'App' },
    { path: '/user-settings', name: 'Configurações', category: 'App' },
    { path: '/checkout-success', name: 'Checkout Sucesso', category: 'Payment' },
    { path: '/admin', name: 'Admin', category: 'Admin' },
    { path: '/admin/*', name: 'Admin (todas)', category: 'Admin' },
    { path: '/maintainance', name: 'Manutenção', category: 'Admin' },
  ];
}
